import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

import { auth, db } from "@/core/services/FirebaseService";
import {
  setDoc,
  doc,
  writeBatch,
  query,
  collection,
  limit,
  orderBy,
  where,
  onSnapshot,
  startAfter,
  Timestamp,
  GeoPoint,
  getDocs,
  Query,
  DocumentData,
  deleteDoc,
} from "firebase/firestore";
import { Unsubscribe } from "@firebase/util";

enum ArticlePageQuery {
  all = "all",
}

interface ArticleStoreState {
  doc: { [key: string]: ArticleDoc };
  pageQuery: { [key in ArticlePageQuery]: Array<string> };
  textQuery: { [key: string]: Array<string> };
}

interface ArticleDoc {
  id: string;
  data: ArticleDocData;
  unsub: Unsubscribe | null;
}

interface ArticleDocData {
  aid: string;
  versionNo: number;
  dateCreated: Timestamp | null;
  imageURL: string;
  tags: Array<string>;
  title: string;
  description: string;
  readingDuration: string;
  author: string;
}

@Module
export default class ArticleModule
  extends VuexModule
  implements ArticleStoreState
{
  errorMessage = null;
  doc = {} as { [key: string]: ArticleDoc };
  pageQuery = {} as { [key in ArticlePageQuery]: Array<string> };
  textQuery = {} as { [key: string]: Array<string> };

  @Mutation
  [Mutations.ARTICLE_SET_DOC](payload: {
    id: string;
    data: DocumentData;
    unsub: Unsubscribe | undefined;
  }) {
    const id = payload.id;
    const data = payload.data;
    const unsub = payload.unsub;

    const docData = {
      aid: id,
      versionNo: data.versionNo,
      dateCreated: data.dateCreated ?? null,
      imageURL: data.imageURL ?? "",
      tags: data.tags ?? [],
      title: data.title ?? "",
      description: data.description ?? "",
      readingDuration: data.readingDuration ?? "",
      author: data.author ?? "",
    } as ArticleDocData;

    if (unsub) {
      this.doc[id] = {
        id: id,
        data: docData,
        unsub: unsub,
      };
    } else if (this.doc[id]) {
      this.doc[id].data = docData;
    } else {
      console.error(`FAILED SETTING ARTICLE DOC`);
    }
  }

  @Mutation
  [Mutations.ARTICLE_SET_PAGE_QUERY](payload: {
    key: ArticlePageQuery;
    ids: Array<string>;
    append: boolean;
  }) {
    const key = payload.key;
    const ids = payload.ids;
    const append = payload.append;

    if (append && this.pageQuery[key]) {
      this.pageQuery[key] = [...this.pageQuery[key], ...ids];
    } else {
      this.pageQuery[key] = ids;
    }
  }

  @Mutation
  [Mutations.ARTICLE_SET_TEXT_QUERY](payload: {
    key: string;
    ids: Array<string>;
    append: boolean;
  }) {
    const key = payload.key;
    const ids = payload.ids;
    const append = payload.append;

    if (append && this.textQuery[key]) {
      this.textQuery[key] = [...this.textQuery[key], ...ids];
    } else {
      this.textQuery[key] = ids;
    }
  }

  @Action
  [Actions.ARTICLE_POPULATE_PAGE_QUERY](payload: {
    key: ArticlePageQuery;
    append: boolean;
  }) {
    const key = payload.key;
    const append = payload.append;

    let dbQuery: Query<DocumentData>;
    switch (key) {
      case ArticlePageQuery.all:
      default:
        dbQuery = query(
          collection(db, "articles"),
          where("versionNo", "==", 0),
          orderBy("dateCreated")
          // limit(50)
        );
        break;
    }

    getDocs(dbQuery)
      .then((dbDocs) => {
        console.log(
          "done",
          dbDocs.docs.map((e) => e.id)
        );
        const ids: Array<string> = [];

        dbDocs.forEach((dbDoc) => {
          ids.push(dbDoc.id);
          if (!this.doc[dbDoc.id]) {
            const unsub = onSnapshot(doc(db, "articles", dbDoc.id), (dbDoc) => {
              this.context.commit(Mutations.ARTICLE_SET_DOC, {
                id: dbDoc.id,
                data: dbDoc.data(),
              });
            });
            this.context.commit(Mutations.ARTICLE_SET_DOC, {
              id: dbDoc.id,
              data: dbDoc.data(),
              unsub: unsub,
            });
          }
        });

        this.context.commit(Mutations.ARTICLE_SET_PAGE_QUERY, {
          key: key,
          ids: ids,
          append: append,
        });
      })
      .catch((error) => console.log("owo", error.message));
  }

  @Action
  async [Actions.ARTICLE_POPULATE_TEXT_QUERY](payload: {
    key: string;
    append: boolean;
  }) {
    const key = payload.key;
    const append = payload.append;

    const dbQuery = query(
      collection(db, "articles"),
      where("versionNo", "==", 0),
      orderBy("farmerName"),
      where("farmerName", "==", key),
      where("farmerName", "==", key + "~"),
      // limit(50)
    );

    const dbDocs = await getDocs(dbQuery);
    const ids: Array<string> = [];

    dbDocs.forEach((dbDoc) => {
      ids.push(dbDoc.id);
      if (!this.doc[dbDoc.id]) {
        this.context.commit(Mutations.ARTICLE_SET_DOC, {
          id: dbDoc.id,
          data: dbDoc.data(),
          unsub: onSnapshot(doc(db, "articles", dbDoc.id), (dbDoc) => {
            this.context.commit(Mutations.ARTICLE_SET_DOC, {
              id: dbDoc.id,
              data: dbDoc.data(),
            });
          }),
        });
      }
    });

    this.context.commit(Mutations.ARTICLE_SET_TEXT_QUERY, {
      key: key,
      ids: ids,
      append: append,
    });
  }

  @Action({ rawError: true })
  [Actions.ARTICLE_UPDATE_DOC](payload: {
    id: string;
    article: { [x: string]: any };
  }) {
    console.log("RBW_UPDATE_DOC");
    const id = payload.id;
    const article = payload.article;

    // Get a new write batch
    const batch = writeBatch(db);

    // Set profile value
    const articleRef = doc(db, "articles", id);
    batch.update(articleRef, article);

    console.log(id, article);

    // Commit the batch
    return batch.commit();
  }

  @Action
  async [Actions.ARTICLE_CREATE](payload: {
    id: string;
    article: DocumentData;
  }) {
    const id = payload.id;
    const article = payload.article;

    // Get a new write batch
    const batch = writeBatch(db);

    if (article) {
      // Set profile value
      const articleRef = doc(db, "articles", id);
      batch.set(articleRef, {
        versionNo: 0,
        ...article,
      });
    }

    // Commit the batch
    return batch.commit();
  }

  @Action
  async [Actions.ARTICLE_DELETE_DOC](payload: { id: string }) {
    const id = payload.id;
    return deleteDoc(doc(db, "articles", id))
  }
}

export { ArticlePageQuery, ArticleStoreState, ArticleDoc, ArticleDocData };
